import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Lock, Send, ArrowLeft, CheckCircle2 } from 'lucide-react';
import axios from '../../services/AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';
import { useSettings } from '../../context/SettingsContext';

/**
 * 403 page rendered by <CapabilityRoute> when the caller's capabilities
 * don't satisfy the route's required scope. Phase 1.3 of the access-
 * control redesign.
 *
 * Distinct from the generic src/jsx/pages/Error403.js (which is a
 * pre-existing English-copy generic forbidden page). This component is
 * contextual: it names the module the user tried to reach and offers a
 * "Kontakta administratör" CTA wired to POST /:companyId/me/request-access.
 *
 * The request-access call is log-only on the backend today (no DB write —
 * see backend me/services/accessRequestService.js for scope rationale).
 * UI feedback nevertheless shows success on 2xx so the user gets a clear
 * "your request was logged, an admin will see it" signal.
 */

// FE↔BE contract — keep in sync with ALLOWED_MODULES in
// backend me/services/accessRequestService.js.
const MODULE_DISPLAY = {
  'time-reporting': 'Hantera tidrapporter',
  'time-reporting.approve': 'Godkänna tidrapporter',
  'expenses': 'Hantera utlägg',
  'expenses.approve': 'Godkänna utlägg',
  'reports': 'Rapporter',
  'admin': 'Administration',
};

export const CapabilityForbidden = ({ module: blockedModule }) => {
  const history = useHistory();
  const { companyId } = useSettings();
  const moduleLabel = MODULE_DISPLAY[blockedModule] || blockedModule || 'den här sidan';

  // Lifted from the auth selector chain; defaults to the company ID if
  // the user happens to have arrived without a populated companies map
  // (e.g., direct URL nav after a refresh-token rehydrate).
  const companyName = useSelector(state =>
    state.auth?.user?.companies?.[companyId]?.companyName || ''
  );

  const [requestStatus, setRequestStatus] = useState('idle'); // idle | sending | sent | error

  const handleGoHome = () => {
    if (companyId) history.push(`/${companyId}/`);
    else history.push('/');
  };

  const handleRequestAccess = async () => {
    if (!companyId || !blockedModule) return;
    setRequestStatus('sending');
    try {
      const url = getApiUrl(`${companyId}/me/request-access`);
      await axios.post(url, { module: blockedModule }, { withCredentials: true });
      setRequestStatus('sent');
    } catch (err) {
      console.error('Failed to send access request:', err);
      setRequestStatus('error');
    }
  };

  return (
    <div className="authincation h-100 p-meddle">
      <div className="container h-100">
        <div className="row justify-content-center h-100 align-items-center">
          <div className="col-md-6">
            <div className="form-input-content text-center error-page">
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <Lock size={56} strokeWidth={1.5} />
              </div>
              <h1 className="error-text font-weight-bold" style={{ marginBottom: 8 }}>
                403
              </h1>
              <h4 style={{ marginBottom: 16 }}>Du saknar behörighet</h4>
              <p style={{ marginBottom: 12 }}>
                Den här sidan kräver <strong>{moduleLabel}</strong>
                {companyName ? <> i <strong>{companyName}</strong></> : null} som
                din användare inte har åtkomst till.
              </p>
              <p className="text-muted" style={{ marginBottom: 24 }}>
                Är det fel? Be en administratör på ditt företag att uppdatera
                din roll under <em>Inställningar → Användare</em>.
              </p>

              <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-primary" onClick={handleGoHome}>
                  <ArrowLeft size={16} style={{ marginRight: 6 }} />
                  Tillbaka till start
                </button>

                {requestStatus === 'sent' ? (
                  <button type="button" className="btn btn-success" disabled>
                    <CheckCircle2 size={16} style={{ marginRight: 6 }} />
                    Begäran skickad
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleRequestAccess}
                    disabled={requestStatus === 'sending' || !blockedModule}
                  >
                    <Send size={16} style={{ marginRight: 6 }} />
                    {requestStatus === 'sending' ? 'Skickar…' : 'Kontakta administratör'}
                  </button>
                )}
              </div>

              {requestStatus === 'error' ? (
                <p className="text-danger" style={{ marginTop: 16, fontSize: '0.9em' }}>
                  Det gick inte att skicka begäran. Försök igen senare eller
                  kontakta administratören direkt.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CapabilityForbidden;
